import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  saveScriptDeliveryTarget,
  scriptDeliveryKeys,
  scriptDeliverySummaryQueryOptions,
} from "@/lib/queries/script-delivery.queries";
import {
  ScriptDeliveryPanel,
  type ScriptDeliveryView,
} from "@/features/script-delivery/components/ScriptDeliveryPanel";

export function ScriptDeliveryPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [view, setView] = useState<ScriptDeliveryView>("month");
  const summaryQuery = useQuery(scriptDeliverySummaryQueryOptions());
  const saveTarget = useMutation({
    mutationFn: ({ tenantId, target }: { tenantId: string; target: number | null }) =>
      saveScriptDeliveryTarget(tenantId, target),
    onSuccess: (summary) => {
      queryClient.setQueryData(scriptDeliveryKeys.summary(), summary);
    },
  });

  if (summaryQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">{t("scriptDelivery.loading")}</p>;
  }

  if (summaryQuery.isError || !summaryQuery.data) {
    return <p className="text-sm text-destructive">{t("scriptDelivery.loadError")}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <ScriptDeliveryPanel
        summary={summaryQuery.data}
        view={view}
        onViewChange={setView}
        savingTenantId={saveTarget.isPending ? saveTarget.variables?.tenantId ?? null : null}
        onCommitTarget={(tenantId, target) => {
          saveTarget.mutate({ tenantId, target });
        }}
      />
      {saveTarget.isError ? (
        <p className="text-sm text-destructive">{t("scriptDelivery.saveError")}</p>
      ) : null}
    </div>
  );
}
